import {Field, Form, Formik} from 'formik';
import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import creditCardType from 'credit-card-type';
import {Notification, Select} from '@wr/ui-patterns';

import {pushAnalyticsEvent} from '../Analytics';
import FieldWrapper from '../../molecules/FieldWrapper';
import SubmitButton from '../../molecules/SubmitButton';
import CardNumberInput from '../../molecules/CardNumberInputField';
import CvvCodeInput from '../../molecules/CvvCodeInputField';
import FormTooltip from '../../molecules/FormTooltip/form-tooltip.component';
import {validateCardNumber, validateExpiryDate, validateCVV} from './optile-form.validators';
import config from '../../../config';
import {getYearsFromNow} from '../../../utils/helpers';
import {MONTHS} from '../../../consts';

const FIELDS = {
    cardNumber: {
        name: 'cardNumber',
        id: 'cardNumber',
        placeholder: '',
        component: CardNumberInput
    },
    expiryMonth: {
        name: 'expiryMonth',
        placeholder: 'MM'
    },
    expiryYear: {
        name: 'expiryYear',
        placeholder: 'YYYY'
    },
    verificationCode: {
        type: 'tel',
        name: 'verificationCode',
        id: 'verificationCode',
        component: CvvCodeInput
    },
    isSavedCard: {
        type: 'checkbox',
        name: 'remember',
        id: 'remember'
    }
};

const DEFAULT_VERIFICATION_CODE_SIZE = 3;

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}

const OptileForm = ({
    onSubmit,
    initialData,
    savedCardIsUsed,
    receivedCardNumber,
    receivedCypeType,
    isSubmitting,
    translations,
    languageCode,
    availableLanguageCodes,
    errorMessage,
    receivedValues
}) => {
    const {
        listId, holderName, amount, currency
    } = initialData;
    const {
        optile: {
            form: {
                labels,
                errors,
                submitButton
            }
        },
        cvvHelpInfo
    } = translations;

    const months = MONTHS;
    const years = getYearsFromNow();

    const [cardType, setCardType] = useState({});
    const [ipAddress, setIpAddress] = useState(null);
    const [cvvHelper, setCvvHelper] = useState(false);
    const [expiryMonth, setExpiryMonth] = useState('');
    const [expiryYear, setExpiryYear] = useState('');
    const [showSaveCard, setShowSaveCard] = useState(false);

    const prevExpiryMonth = usePrevious(expiryMonth);
    const prevExpiryYear = usePrevious(expiryYear);

    const receivedCodeSize = receivedCardNumber && creditCardType(receivedCardNumber)[0].code.size;
    const verificationCodeSize = receivedCodeSize || cardType.code?.size || DEFAULT_VERIFICATION_CODE_SIZE;

    const initialValues = {
        [FIELDS.cardNumber.name]: '',
        [FIELDS.expiryMonth.name]: '',
        [FIELDS.expiryYear.name]: '',
        [FIELDS.verificationCode.name]: '',
        [FIELDS.isSavedCard.name]: false,
        ...receivedValues
    };

    function onSubmitClick(err) {
        if (Object.keys(err).length > 0) {
            pushAnalyticsEvent({
                interactionName: 'form error',
                formName: 'new-optile-payment-form',
                formField: Object.keys(errors).join(','),
                formMessage: Object.values(errors).join(',')
            });
        } else {
            pushAnalyticsEvent({
                interactionName: 'form tracking',
                formName: 'new-optile-payment-form',
                formField: 'pay-button'
            });
        }
    }

    const fetchIpAddress = () => fetch('/ip')
        .then(response => response.json())
        .then(data => {
            setIpAddress(data.ip);
        })
        .catch(() => {
            setIpAddress('');
        });


    const handleSubmit = ({cardNumber, verificationCode, remember}) => {
        pushAnalyticsEvent({
            interactionName: 'form submit',
            formName: 'new-optile-payment-form'
        });

        if (typeof window.opEncrypt === 'undefined') {
            throw new Error('opEncrypt script does not exist on window');
        }

        if (remember) {
            pushAnalyticsEvent({
                interactionName: 'form tracking',
                formName: 'new-optile-payment-form',
                formField: 'savedCard checkbox'
            });
        }

        const {userAgent} = navigator;

        // form data
        const {type: currentCardType} = cardType;
        const timestamp = new Date();
        const cardNumberWithoutSpaces = cardNumber.replace(/\s/g, '');

        // variants of sent form data:
        // -- with saved card - verificationCode
        // -- without saved card - holderName, number, verificationCode, expiryMonth, expiryYear
        const sentData = () => {
            if (savedCardIsUsed) {
                return {
                    verificationCode
                };
            }

            return {
                holderName,
                number: cardNumberWithoutSpaces,
                verificationCode,
                expiryMonth,
                expiryYear
            };
        };

        // encrypting form data
        window.opEncrypt(
            sentData(),
            {
                kty: 'RSA',
                n: config.optile.nKey,
                e: config.optile.eKey
            },
            timestamp,
            listId
        ).then(encryptedAccount => {
            // format card type if saved-card
            function getFormatCardType(type) {
                if (type === 'AMEX') {
                    return 'american-express';
                }
                if (type === 'DINERS') {
                    return 'diners-club';
                }
                return type.toLowerCase();
            }

            // format card type if a new card
            function getShortCardType(type) {
                if (type === 'american-express') {
                    return 'AMEX';
                }
                if (type === 'diners-club') {
                    return 'DINERS';
                }
                return type.toUpperCase();
            }

            // mask of card number
            const cardNumberMasked = receivedCardNumber ?
                receivedCardNumber.slice(-4) : cardNumberWithoutSpaces.slice(-4);


            onSubmit({
                languageCode,
                availableLanguageCodes,
                encryptedAccount,
                cardType: receivedCypeType ? getFormatCardType(receivedCypeType) : getShortCardType(currentCardType),
                userAgent,
                ipAddress,
                cardNumberMasked,
                expirationMonth: expiryMonth,
                expirationYear: expiryYear,
                remember
            });
        });
    };

    const handleCardTypeChange = newCardType => {
        setCardType(newCardType);
    };

    const handleHelper = () => {
        pushAnalyticsEvent({
            interactionName: 'click help button',
            formName: 'new-optile-payment-form',
            formField: 'cvv-helper-button'
        });
        setCvvHelper(!cvvHelper);
    };

    useEffect(() => {
        if (ipAddress === null) {
            fetchIpAddress();
        }
    }, [ipAddress]);

    useEffect(() => {
        // 'savedCardIsUsed === true' when user doesn't use the saved card or the dates change for the saved card
        if (!savedCardIsUsed || prevExpiryMonth !== expiryMonth || prevExpiryYear !== expiryYear) {
            setShowSaveCard(true);
        }
    }, [expiryMonth, expiryYear, prevExpiryMonth, prevExpiryYear, savedCardIsUsed]);

    return (
        <div>
            {errorMessage && (
                <div className="optile-form-error-container">
                    <Notification messages={[errorMessage]} type="error" />
                </div>
            )}
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
            >
                {({values, validateForm}) => (
                    <Form className="optile-form" autoComplete="off">
                        {/* Card number */}
                        {!savedCardIsUsed && (
                            <div className="flex-row">
                                <FieldWrapper
                                    name={FIELDS.cardNumber.name}
                                    label={labels.cardNumber}
                                >
                                    <Field
                                        className="optile-form-input"
                                        {...FIELDS.cardNumber}
                                        validate={validateCardNumber(errors.cardNumber)}
                                        onCardTypeChange={handleCardTypeChange}
                                    />
                                </FieldWrapper>
                            </div>
                        )}

                        {/* Date and CVV-code */}
                        <div className="flex-row">
                            {/* Date */}
                            <div className="flex-column">
                                <div className="optile-form-date">
                                    <FieldWrapper name={FIELDS.expiryMonth.name} label={labels.expiryDate}>
                                        <div className="optile-select-wrapper">
                                            <Field
                                                name={FIELDS.expiryMonth.name}
                                                // eslint-disable-next-line max-len
                                                validate={month => validateExpiryDate(errors.expiryDate)({month, year: values[FIELDS.expiryYear.name]})}
                                            >
                                                {({field, form: {setFieldValue}}) => (
                                                    <Select
                                                        {...field}
                                                        options={months.map(month => ({id: month, label: month}))}
                                                        optionValuePropertyName="label"
                                                        optionTextPropertyName="id"
                                                        onChange={value => {
                                                            setExpiryMonth(value);
                                                            setFieldValue(FIELDS.expiryMonth.name, value);
                                                        }}
                                                        placeholder={FIELDS.expiryMonth.placeholder}
                                                    />
                                                )}
                                            </Field>
                                            <Field name={FIELDS.expiryYear.name}>
                                                {({field, form: {setFieldValue}}) => (
                                                    <Select
                                                        {...field}
                                                        options={years.map(year => ({id: year, label: year}))}
                                                        optionValuePropertyName="label"
                                                        optionTextPropertyName="id"
                                                        onChange={value => {
                                                            setExpiryYear(value);
                                                            setFieldValue(FIELDS.expiryYear.name, value);
                                                        }}
                                                        placeholder={FIELDS.expiryYear.placeholder}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                    </FieldWrapper>
                                </div>
                            </div>

                            {/* CVV-code */}
                            <div className="flex-column">
                                <FieldWrapper
                                    name={FIELDS.verificationCode.name}
                                    label={labels.cvcCode}
                                >
                                    <Field
                                        className="optile-form-input"
                                        {...FIELDS.verificationCode}
                                        maxLength={verificationCodeSize}
                                        validate={validateCVV(errors.cvvCode, verificationCodeSize)}
                                        isHelper
                                        onClickHelp={handleHelper}
                                    />
                                    {cvvHelper && (
                                        <div className="optile-form-helper">
                                            <FormTooltip
                                                title={verificationCodeSize === 3 ?
                                                    cvvHelpInfo.titleThreeDigits :
                                                    cvvHelpInfo.titleFourDigits}
                                                text={cvvHelpInfo.text}
                                                onClose={handleHelper}
                                            />
                                        </div>
                                    )}
                                </FieldWrapper>
                            </div>
                        </div>

                        {/* Checkbox for save card */}
                        {showSaveCard && (
                            <div className="flex-row">
                                <div className="optile-form-checkbox">
                                    <div className="checkbox">
                                        <Field
                                            className="optile-form-checkbox"
                                            {...FIELDS.isSavedCard}
                                        />
                                        <label htmlFor={FIELDS.isSavedCard.id}>{labels.isSavedCard}</label>
                                    </div>
                                </div>
                            </div>
                        )}

                        <SubmitButton
                            isSubmitting={isSubmitting}
                            onClick={() => validateForm().then(err => onSubmitClick(err))}
                        >
                            <span>
                                {submitButton}
                                {' '}
                                {amount.toFixed(2)}
                                {' '}
                                {currency}
                            </span>
                        </SubmitButton>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

OptileForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    initialData: PropTypes.shape({
        listId: PropTypes.string.isRequired,
        holderName: PropTypes.string.isRequired,
        amount: PropTypes.number,
        currency: PropTypes.string
    }),
    savedCardIsUsed: PropTypes.bool,
    receivedCardNumber: PropTypes.string,
    receivedCypeType: PropTypes.string,
    isSubmitting: PropTypes.bool,
    translations: PropTypes.shape({}).isRequired,
    languageCode: PropTypes.string.isRequired,
    availableLanguageCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
    errorMessage: PropTypes.string,
    receivedValues: PropTypes.shape({})
};

OptileForm.defaultProps = {
    initialData: {
        paymentAmount: 0,
        currency: ''
    },
    savedCardIsUsed: false,
    receivedCardNumber: '',
    receivedCypeType: '',
    isSubmitting: false,
    errorMessage: '',
    receivedValues: {}
};

export default OptileForm;
